// Admin Settings UI for the time-off feature (T-018, plan §6.7 follow-up).
//
// Surfaces what shipped in T-013 (schema + seed) and T-014 (CRUD endpoints):
// the eight default `TimeOffPolicy` rows and the fourteen DK 2026
// `PublicHoliday` rows. Only renders for OWNER + ADMIN — for MEMBER users
// the parent SettingsView simply doesn't include this section.
//
// Server endpoints:
//   GET    /api/admin-config/timeoff-policies
//   PATCH  /api/admin-config/timeoff-policies/:id   (rename, archive, sortOrder)
//   GET    /api/admin-config/public-holidays
//   POST   /api/admin-config/public-holidays
//   PATCH  /api/admin-config/public-holidays/:id    (archive)

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Archive, ArchiveRestore, Pencil, Check, X, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import type { TimeOffPolicy, PublicHoliday } from '@pa/shared';

type Site = { id: string; name: string };

// ── Policies ─────────────────────────────────────────────────────────

function PoliciesPanel() {
  const { me } = useAuth();
  const isAdmin = me?.user?.role === 'OWNER' || me?.user?.role === 'ADMIN';
  const qc = useQueryClient();
  const policies = useQuery<{ items: TimeOffPolicy[] }>({
    queryKey: ['adminConfig', 'timeoff-policies'],
    queryFn: () => api.get('/api/admin-config/timeoff-policies'),
  });

  // One row at a time can be in rename mode. Local state keeps the draft
  // name so the user can hit Esc / Cancel without losing the rest of the
  // list. Server is the source of truth — on save we PATCH and let
  // TanStack Query refetch.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.patch<TimeOffPolicy>(`/api/admin-config/timeoff-policies/${id}`, {
        name,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['adminConfig', 'timeoff-policies'] }),
  });

  const archiveMutation = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      api.patch<TimeOffPolicy>(`/api/admin-config/timeoff-policies/${id}`, {
        archived,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['adminConfig', 'timeoff-policies'] }),
  });

  if (policies.isLoading) {
    return <p className="text-sm text-slate-400">Loading policies…</p>;
  }
  if (policies.isError) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-rose-600">
          Couldn&apos;t load time-off policies. Something went wrong on our end.
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => policies.refetch()}
            disabled={policies.isFetching}
            className="inline-flex items-center gap-1 text-sm font-medium text-purple-700 hover:bg-purple-50 px-3 py-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw size={14} />
            {policies.isFetching ? 'Retrying…' : 'Retry'}
          </button>
          {isAdmin && (
            <span className="text-xs text-slate-500">
              If this persists, <Link to="/settings" className="text-purple-700 hover:underline">refresh the page</Link> or contact support.
            </span>
          )}
        </div>
      </div>
    );
  }

  const items = policies.data?.items ?? [];

  return (
    <div className="divide-y divide-slate-100">
      {items.map((p) => {
        const isEditing = editingId === p.id;
        return (
          <div
            key={p.id}
            data-testid={`policy-row-${p.id}`}
            className={`flex items-center gap-3 py-3 ${
              p.archived ? 'opacity-60' : ''
            }`}
          >
            <span className="text-2xl flex-shrink-0" aria-hidden>
              {p.emoji}
            </span>
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <input
                  className="w-full text-sm font-medium px-2 py-1 border border-purple-300 rounded-lg focus:outline-none focus:border-purple-500"
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setEditingId(null);
                    if (e.key === 'Enter' && draftName.trim()) {
                      renameMutation.mutate({ id: p.id, name: draftName.trim() });
                      setEditingId(null);
                    }
                  }}
                  autoFocus
                />
              ) : (
                <p className="text-sm font-medium truncate">
                  {p.name}
                  {p.archived && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">
                      archived
                    </span>
                  )}
                </p>
              )}
              <p className="text-[11px] text-slate-400 truncate">
                {p.balanceType} · {p.accrualRule} · {p.approvalFlow}
              </p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {isEditing ? (
                <>
                  <button
                    type="button"
                    aria-label="Save"
                    className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600"
                    onClick={() => {
                      if (draftName.trim()) {
                        renameMutation.mutate({ id: p.id, name: draftName.trim() });
                        setEditingId(null);
                      }
                    }}
                  >
                    <Check size={16} />
                  </button>
                  <button
                    type="button"
                    aria-label="Cancel"
                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
                    onClick={() => setEditingId(null)}
                  >
                    <X size={16} />
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    aria-label={`Rename ${p.name}`}
                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
                    onClick={() => {
                      setEditingId(p.id);
                      setDraftName(p.name);
                    }}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    aria-label={p.archived ? `Unarchive ${p.name}` : `Archive ${p.name}`}
                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
                    onClick={() =>
                      archiveMutation.mutate({ id: p.id, archived: !p.archived })
                    }
                  >
                    {p.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                  </button>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Holidays ─────────────────────────────────────────────────────────

function HolidaysPanel() {
  const qc = useQueryClient();
  const holidays = useQuery<{ items: PublicHoliday[] }>({
    queryKey: ['adminConfig', 'public-holidays'],
    queryFn: () => api.get('/api/admin-config/public-holidays'),
  });
  // Sites for the optional siteId picker on Add. Sites are tiny and rarely
  // change, so caching keeps the UI snappy.
  const sites = useQuery<{ items: Site[] }>({
    queryKey: ['adminConfig', 'sites'],
    queryFn: () => api.get('/api/admin-config/sites'),
    staleTime: 5 * 60 * 1000,
  });

  const archiveMutation = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      api.patch<PublicHoliday>(`/api/admin-config/public-holidays/${id}`, {
        archived,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['adminConfig', 'public-holidays'] }),
  });

  // Add-form state. Hidden by default; opens via the "+ Add holiday" button.
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDate, setNewDate] = useState('');
  const [newSiteId, setNewSiteId] = useState<string>(''); // empty = org-wide
  const [addError, setAddError] = useState<string | null>(null);

  const addMutation = useMutation({
    mutationFn: () =>
      api.post<PublicHoliday>('/api/admin-config/public-holidays', {
        name: newName.trim(),
        date: newDate,
        siteId: newSiteId || null,
      }),
    onSuccess: () => {
      setAdding(false);
      setNewName('');
      setNewDate('');
      setNewSiteId('');
      setAddError(null);
      qc.invalidateQueries({ queryKey: ['adminConfig', 'public-holidays'] });
    },
    onError: (err) => {
      // The API returns either { error: 'duplicate', field } or the standard
      // validation_error body. Surface a friendly message either way.
      if (err && typeof err === 'object' && 'body' in err) {
        const body = (err as { body?: { error?: string; issues?: { fieldErrors?: Record<string, string[]> } } }).body;
        if (body?.error === 'duplicate') {
          setAddError('A holiday with this date and name already exists.');
          return;
        }
        const fieldErrs = body?.issues?.fieldErrors;
        if (fieldErrs) {
          const first = Object.values(fieldErrs)[0]?.[0];
          if (first) {
            setAddError(first);
            return;
          }
        }
      }
      setAddError('Could not add the holiday. Please try again.');
    },
  });

  if (holidays.isLoading) {
    return <p className="text-sm text-slate-400">Loading holidays…</p>;
  }
  if (holidays.isError) {
    return (
      <p className="text-sm text-rose-600">
        Couldn&apos;t load public holidays. Refresh the page to try again.
      </p>
    );
  }

  const items = holidays.data?.items ?? [];
  const sitesById = new Map((sites.data?.items ?? []).map((s) => [s.id, s.name]));

  return (
    <div>
      {!adding && (
        <button
          type="button"
          className="inline-flex items-center gap-2 text-sm font-medium text-purple-700 hover:bg-purple-50 px-3 py-1.5 rounded-lg mb-3"
          onClick={() => setAdding(true)}
        >
          <Plus size={14} /> Add holiday
        </button>
      )}
      {adding && (
        <form
          className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            setAddError(null);
            if (!newName.trim() || !/^\d{4}-\d{2}-\d{2}$/.test(newDate)) {
              setAddError('Both name and a YYYY-MM-DD date are required.');
              return;
            }
            addMutation.mutate();
          }}
        >
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="Holiday name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={120}
              required
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
              data-testid="holiday-name-input"
            />
            <input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              required
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
              data-testid="holiday-date-input"
            />
          </div>
          <select
            value={newSiteId}
            onChange={(e) => setNewSiteId(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
            data-testid="holiday-site-select"
          >
            <option value="">Org-wide (every site)</option>
            {(sites.data?.items ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} only
              </option>
            ))}
          </select>
          {addError && <p className="text-xs text-rose-600">{addError}</p>}
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setAddError(null);
              }}
              className="px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={addMutation.isPending}
              className="px-4 py-1.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg disabled:opacity-50"
            >
              {addMutation.isPending ? 'Adding…' : 'Add holiday'}
            </button>
          </div>
        </form>
      )}
      <div className="divide-y divide-slate-100">
        {items.map((h) => (
          <div
            key={h.id}
            data-testid={`holiday-row-${h.id}`}
            className={`flex items-center gap-3 py-2 ${h.archived ? 'opacity-60' : ''}`}
          >
            <span className="text-xs font-mono text-slate-500 flex-shrink-0 w-24">
              {new Date(h.date).toISOString().slice(0, 10)}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm truncate">
                {h.name}
                {h.archived && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">
                    archived
                  </span>
                )}
              </p>
              <p className="text-[11px] text-slate-400">
                {h.siteId ? sitesById.get(h.siteId) ?? 'Site' : 'Org-wide'}
              </p>
            </div>
            <button
              type="button"
              aria-label={h.archived ? `Unarchive ${h.name}` : `Archive ${h.name}`}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 flex-shrink-0"
              onClick={() => archiveMutation.mutate({ id: h.id, archived: !h.archived })}
            >
              {h.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Public component ─────────────────────────────────────────────────

export default function TimeOffSettings() {
  return (
    <>
      <section className="bg-white border border-slate-100 rounded-2xl p-6">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-slate-400 mb-1">
          Time-off policies
        </h2>
        <p className="text-xs text-slate-500 mb-4">
          The leave types employees can request. Rename, archive, or restore — every change is
          audited and applies organization-wide. Archived policies stay readable on existing
          requests but disappear from new-request dropdowns.
        </p>
        <PoliciesPanel />
      </section>

      <section className="bg-white border border-slate-100 rounded-2xl p-6">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-slate-400 mb-1">
          Public holidays
        </h2>
        <p className="text-xs text-slate-500 mb-4">
          Days the working-day calculation should treat as non-working. Org-wide holidays apply
          to every site; site-specific ones only apply to that office.
        </p>
        <HolidaysPanel />
      </section>
    </>
  );
}
